import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, model, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { StoragePlaceDropdownComponent } from '../../../../warehouse/controls/storage-place-dropdown/storage-place-dropdown.component';
import { ProductStockChartDiagramComponent } from '../../../../warehouse/modules/product-stock-chart-diagram/product-stock-chart-diagram.component';
import { LIST_PAGE_TABS_FILL_HEIGHT_CLASS } from '../../../../../shared/layout/page-fill-host.classes';
import { I18nService } from '../../../../../@core/services/i18n/i18n.service';
import { ModalService } from '../../../../../@core/services/modal/modal.service';
import { GetProductDispositionsQuery, Product, ProductDisposition, ProductDispositionSearchInput } from '../../../../../generated/generated-types';
import { PhxStatCardColor } from '../../../../../shared/components/phx-stat-card/phx-stat-card-theme';
import { PhxStatCardComponent } from '../../../../../shared/components/phx-stat-card/phx-stat-card.component';
import { FormlyFillHeightListHostDirective } from '../../../../custom-formly/formly-tabs/formly-fill-height-list-host.directive';
import { WidgetWarehouseDispositionWorkspaceComponent } from '../../../../documents/components/warehouse-disposition/widget-warehouse-disposition-workspace/widget-warehouse-disposition-workspace.component';
import { WidgetManualStockTransactionComponent } from '../../../../warehouse/components/widget-manual-stock-transaction/widget-manual-stock-transaction.component';
import { ProductDispositionService } from '../../../services/product-disposiiton.service';
import { ProductDispositionOverviewPanelComponent } from '../../product-disposition-overview-list/product-disposition-overview-panel/product-disposition-overview-panel.component';
import { ProductStockLineChartComponent } from '../product-stock-line-chart/product-stock-line-chart.component';
import { ProductStorageSettingsComponent } from '../product-storage-settings/product-storage-settings.component';
import { WidgetProductNotBackedByStockListComponent } from '../widget-product-not-backed-by-stock-list/widget-product-not-backed-by-stock-list.component';
import { WidgetProductReservedOrderedListComponent } from '../widget-product-reserved-ordered-list/widget-product-reserved-ordered-list.component';
import { WidgetProductStorageTimelineComponent } from '../widget-product-storage-timeline/widget-product-storage-timeline.component';


class CardInfoHolder {
  title: string;
  color: PhxStatCardColor;
  icon: string;
  value: string | null | undefined;
  tooltip: string;
  onClick?: () => void;
}

@Component({
  selector: 'app-widget-product-storage-overview',
  templateUrl: './widget-product-storage-overview.component.html',
  hostDirectives: [FormlyFillHeightListHostDirective],
  standalone: true,
  imports: [
    StoragePlaceDropdownComponent,
    TabsModule,
    TranslatePipe,
    PhxStatCardComponent,
    ProductStockChartDiagramComponent,
    CommonModule,
    ProductStorageSettingsComponent,
    WidgetProductStorageTimelineComponent,
    ProductStockLineChartComponent,
    TooltipModule,
    WidgetWarehouseDispositionWorkspaceComponent,
    WidgetManualStockTransactionComponent,
    ProductDispositionOverviewPanelComponent,
  ],
  providers: [ProductDispositionService]
})
export class WidgetProductStorageOverviewComponent extends FieldType<FieldTypeConfig> implements OnInit {
  readonly listPageTabsClass = LIST_PAGE_TABS_FILL_HEIGHT_CLASS;

  private static readonly FILL_HEIGHT_TAB_VALUES = new Set(['1', '2', '3', '5', '6']);

  productId = model<string>();
  product = model<Product>();

  storagePlaceIdFilter = signal<string | undefined>(undefined);
  activeTabIndex = signal(0);
  activeTabValue = signal<string | number>('0');

  cards = signal<CardInfoHolder[]>([]);

  cardTotal = new CardInfoHolder();
  cardOpenSales = new CardInfoHolder();
  cardReserved = new CardInfoHolder();
  cardOpenRequested = new CardInfoHolder();
  cardOrdered = new CardInfoHolder();
  cardQuarantine = new CardInfoHolder();
  cardNotBackedByStock = new CardInfoHolder();

  private readonly destroyRef = inject(DestroyRef);
  private cardDispositionSub?: Subscription;

  constructor(
    private i18n: I18nService,
    private modalSerivce: ModalService,
    private productDispositionService: ProductDispositionService
  ) {
    super();
  }

  ngOnInit() {

    this.form.valueChanges.subscribe((x: Product) => {

      const model: Product = this.model;

      if (x && model && model.id && model.id !== this.productId()) {
        this.productId.set(model.id);
        this.watchCardInfo();
      }
      this.product.set(x);
      this.rebuildCards();
    });

    this.product.set(this.model);


    if (this.model?.id) {
      this.productId.set(this.model.id);
      this.watchCardInfo();
    }

    this.initCardMetadata();
    this.rebuildCards();
  }

  private initCardMetadata(): void {
    this.cardTotal.title = this.i18n.translate(_('product.totalStock'));
    this.cardTotal.color = 'teal';
    this.cardTotal.icon = 'cube';
    this.cardTotal.tooltip = this.i18n.translate(_('product.totalStockTooltip'));

    this.cardOpenSales.title = this.i18n.translate(_('product.openSales'));
    this.cardOpenSales.color = 'green';
    this.cardOpenSales.tooltip = this.i18n.translate(_('product.openSalesTooltip'));
    this.cardOpenSales.icon = 'cube';

    this.cardOrdered.title = this.i18n.translate(_('product.ordered'));
    this.cardOrdered.color = 'blue';
    this.cardOrdered.tooltip = this.i18n.translate(_('product.orderedTooltip'));
    this.cardOrdered.icon = 'hourglass-half';
    this.cardOrdered.onClick = () => {
      this.modalSerivce.fromComponent(WidgetProductReservedOrderedListComponent, {
        size: 'lg',
        closable: true,
        title: this.i18n.translate(_('product.ordered')),
        setLocalsSig: {
          searchInputSig: { ...this.getSearchInput(), filterType: 'ORDERED' }
        }
      }).subscribe();
    };

    this.cardQuarantine.title = this.i18n.translate(_('product.quarantine'));
    this.cardQuarantine.color = 'red';
    this.cardQuarantine.tooltip = this.i18n.translate(_('product.quarantineTooltip'));
    this.cardQuarantine.icon = 'xmark';

    this.cardNotBackedByStock.title = this.i18n.translate(_('product.notBackedByStock'));
    this.cardNotBackedByStock.color = 'indigo';
    this.cardNotBackedByStock.tooltip = this.i18n.translate(_('product.notBackedByStockTooltip'));
    this.cardNotBackedByStock.icon = 'circle-question';
    this.cardNotBackedByStock.onClick = () => {
      const productId = this.productId();
      this.modalSerivce.fromComponent(WidgetProductNotBackedByStockListComponent, {
        size: 'lg',
        closable: true,
        title: this.i18n.translate(_('product.notBackedByStock')),
        setLocalsSig: {
          searchInputSig: {
            productIds: productId ? [productId] : [],
            storagePlaceIds: this.storagePlaceIdFilter() ? [this.storagePlaceIdFilter()!] : undefined
          }
        }
      }).subscribe();
    };

    this.cardReserved.title = this.i18n.translate(_('product.reserved'));
    this.cardReserved.color = 'orange';
    this.cardReserved.tooltip = this.i18n.translate(_('product.reservedTooltip'));
    this.cardReserved.icon = 'circle-pause';
    this.cardReserved.onClick = () => {
      this.modalSerivce.fromComponent(WidgetProductReservedOrderedListComponent, {
        size: 'lg',
        closable: true,
        title: this.i18n.translate(_('product.reserved')),
        setLocalsSig: {
          searchInputSig: { ...this.getSearchInput(), filterType: 'RESERVED' }
        }
      }).subscribe();
    };

    this.cardOpenRequested.title = this.i18n.translate(_('product.openRequested'));
    this.cardOpenRequested.color = 'rose';
    this.cardOpenRequested.tooltip = this.i18n.translate(_('product.openRequestedTooltip'));
    this.cardOpenRequested.icon = 'scale-unbalanced';
    this.cardOpenRequested.onClick = () => {
      this.modalSerivce.fromComponent(WidgetProductReservedOrderedListComponent, {
        size: 'lg',
        closable: true,
        title: this.i18n.translate(_('product.openRequested')),
        setLocalsSig: {
          searchInputSig: { ...this.getSearchInput(), filterType: 'OPEN_REQUESTED' }
        }
      }).subscribe();
    };
  }

  private rebuildCards(): void {
    const cardsArray: CardInfoHolder[] = [
      this.cardTotal,
      this.cardOpenSales,
      this.cardOrdered,
      this.cardQuarantine,
      this.cardNotBackedByStock,
      this.cardReserved,
      this.cardOpenRequested,
    ];

    this.cards.set(cardsArray);
  }

  getSearchInput(): ProductDispositionSearchInput {
    const productId = this.productId();
    return {
      ignoreLot: true,
      ignoreSerial: true,
      productIds: productId ? [productId] : [],
      ignoreStoragePlace: true,
      storagePlaceIds: this.storagePlaceIdFilter() ? [this.storagePlaceIdFilter()!] : undefined
    };
  }

  warehouseChanged(id: string | undefined) {
    // Normalise falsy ids to undefined: the chart checks `storagePlaceId != null`, so an empty
    // string would be passed on as storagePlaceIds: [''] and empty the diagram, while the cards
    // (which check truthiness) would still aggregate over everything.
    this.storagePlaceIdFilter.set(id || undefined);
    this.watchCardInfo();
  }

  onActiveTabChange(value: string | number): void {
    this.activeTabValue.set(value);
    this.activeTabIndex.set(+value);
  }

  /** Lager › Einstellungen: no form control for the disposition type – write the model and let the detail page see the change. */
  onDispositionTypeChange(value: Product['dispositionType']): void {
    if (!this.model || this.model.dispositionType === value)
      return;
    this.model.dispositionType = value;
    this.form.markAsDirty();
    this.options?.formState?.detailComponent?.notifyDirtyRecheck?.();
  }

  isFillHeightListTabActive(): boolean {
    return WidgetProductStorageOverviewComponent.FILL_HEIGHT_TAB_VALUES.has(String(this.activeTabIndex()));
  }

  /**
   * Keeps GetProductDispositions active so a stock-transaction refetchQueries
   * updates these cards. Restarts when product or storage place changes.
   */
  private watchCardInfo(): void {
    this.cardDispositionSub?.unsubscribe();

    if (!this.productId()) {
      return;
    }

    this.cardDispositionSub = this.productDispositionService.getItems(this.getSearchInput())
      .mapStream(x => x.getProductDispositions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pds => this.applyCardDisposition(pds));
  }

  private applyCardDisposition(pds: GetProductDispositionsQuery['getProductDispositions']): void {
    if (pds.totalItems > 0) {
      const pd: ProductDisposition = pds.items[0];

      this.cardTotal.value = pd.totalStockQuantity?.toString() ?? null;
      this.cardOpenSales.value = pd.openSalesQuantity?.toString() ?? null;
      this.cardReserved.value = pd.reservedQuantity?.toString() ?? null;
      this.cardOpenRequested.value = pd.openRequestedQuantity?.toString() ?? null;
      this.cardOrdered.value = pd.orderedQuantity?.toString() ?? null;
      this.cardQuarantine.value = pd.quarantineQuantity?.toString() ?? null;
      this.cardNotBackedByStock.value = pd.notBackedByStockQuantity?.toString() ?? null;
    } else {
      this.cardTotal.value = '0';
      this.cardOpenSales.value = '0';
      this.cardReserved.value = '0';
      this.cardOpenRequested.value = '0';
      this.cardOrdered.value = '0';
      this.cardQuarantine.value = '0';
      this.cardNotBackedByStock.value = '0';
    }

    this.cards.update(c => [...c]);
  }

}
